// EmployeeManagementPage.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  FaDownload, 
  FaUsers, 
  FaChevronLeft, 
  FaChevronRight, 
  FaMoneyBillWave, 
  FaFileInvoiceDollar, 
  FaSearch,
  FaFilter,
  FaTimes
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import styles from './EmployeeFinanceManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../../../contexts/ThemeContext';
import './EmployeeFinanceManagementDarkMode.css'; // Import dark mode styles

// Imported modal components
import SalaryModal from './SalaryModal';
import PaymentsModal from './PaymentsModal';
import BudgetPricingModal from './BudgetPricingModal';
import ZamModal from './ZamModal';
import TableFilter from '../../../components/TableFilter';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const EmployeeManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showBudgetPricingModal, setShowBudgetPricingModal] = useState(false);
  const [showZamModal, setShowZamModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [filters, setFilters] = useState({ Active: '1' });
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    eName: true,
    eSurname: true,
    tcNumber: true,
    phone: true,
    departman: true,
    Active: true,
    address: false,
    annualPermit: false,
    birthday: false,
    eMail: false,
    isHandicappedPerson: false,
    isIntern: false,
    isRetired: false,
    languageInfo: false,
    nationality: false,
    profession: false,
    educationInfo: false,
    startDate: false,
    workType: false
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    eName: { label: 'Ad', type: 'text' },
    eSurname: { label: 'Soyad', type: 'text' },
    tcNumber: { label: 'TC No', type: 'text' },
    phone: { label: 'Telefon', type: 'text' },
    departman: { label: 'Departman', type: 'text' },
    Active: {
      label: 'Durum',
      type: 'select',
      options: [
        { value: 1, label: 'Aktif' },
        { value: 0, label: 'İnaktif' }
      ]
    },
    address: { label: 'Ev Adresi', type: 'text' },
    annualPermit: { label: 'Yıllık İzin', type: 'text' },
    birthday: { label: 'Doğum Tarihi', type: 'date' },
    eMail: { label: 'E-posta', type: 'text' },
    isHandicappedPerson: {
      label: 'Engellilik Durumu',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    isIntern: {
      label: 'Stajyer',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    isRetired: {
      label: 'Emekli',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    languageInfo: { label: 'Dil Bilgisi', type: 'text' },
    nationality: { label: 'Uyruk', type: 'text' },
    profession: { label: 'Meslek', type: 'text' },
    educationInfo: {
      label: 'Eğitim Bilgisi',
      type: 'select',
      options: [
        { value: 'Ortaokul / Ortaöğretim', label: 'Ortaokul / Ortaöğretim' },
        { value: 'Lise', label: 'Lise' },
        { value: 'Ön Lisans (2 Yıllık Üniversite)', label: 'Ön Lisans (2 Yıllık Üniversite)' },
        { value: 'Lisans (4 Yıllık Üniversite)', label: 'Lisans (4 Yıllık Üniversite)' },
        { value: 'Yüksek Lisans (Master)', label: 'Yüksek Lisans (Master)' },
        { value: 'Doktora (PhD)', label: 'Doktora (PhD)' }
      ]
    },
    startDate: { label: 'Başlangıç Tarihi', type: 'date' },
    workType: {
      label: 'Çalışma Şekli',
      type: 'select',
      options: [
        { value: 'Full Time', label: 'Tam Zamanlı' },
        { value: 'Part Time', label: 'Yarı Zamanlı' }
      ]
    }
  };

  const fetchEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Error fetching employees');
    }
  };

  useEffect(() => {
    fetchEmployees();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    let filtered = employees;

    // Apply search term first (searches name, surname, and TC number)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        emp =>
          (emp.eName && emp.eName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.eSurname && emp.eSurname.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.tcNumber && emp.tcNumber.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Active') {
          filtered = filtered.filter(item =>
            String(item[key]) === String(filters[key])
          );
        } else {
          filtered = filtered.filter(item =>
            String(item[key])
              ?.toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    // Sort alphabetically by eName (Ad column)
    filtered = filtered.sort((a, b) => {
      const nameA = (a.eName || '').toString().toLowerCase();
      const nameB = (b.eName || '').toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setFilteredEmployees(filtered);
  }, [employees, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = employees.filter(emp => emp.Active === 1).length;
  const inactiveCount = employees.filter(emp => emp.Active !== 1).length;

  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Employees');
      worksheet.columns = [
        { header: 'First Name', key: 'eName', width: 15 },
        { header: 'Last Name', key: 'eSurname', width: 15 },
        { header: 'TC No', key: 'tcNumber', width: 15 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Department', key: 'departman', width: 20 },
        { header: 'Status', key: 'Active', width: 10 }
      ];
      employees.forEach(emp => {
        worksheet.addRow({
          eName: truncateText(emp.eName),
          eSurname: truncateText(emp.eSurname),
          tcNumber: truncateText(emp.tcNumber),
          phone: truncateText(emp.phone),
          departman: truncateText(emp.departman),
          Active: emp.Active === 1 ? 'Active' : 'Inactive'
        });
      });
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Error downloading file');
    }
  };

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  const handleStatusToggle = async () => {
    if (!selectedEmployee) return;
    const newStatus = selectedEmployee.Active === 1 ? 0 : 1;
    try {
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);
      await updateDoc(employeeRef, { Active: newStatus });
      toast.success(`Çalışan durumu ${newStatus === 1 ? 'Aktif' : 'İnaktif'} olarak güncellendi`);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const openSalaryModal = async () => {
    if (!selectedEmployee) return;
    setShowSalaryModal(true);
  };
  
  const openPaymentsModal = () => setShowPaymentsModal(true);
  
  const openBudgetPricingModal = () => setShowBudgetPricingModal(true);

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [employees, filteredEmployees]);

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Add this new function to handle column resizing
  const handleColumnResize = (e, columnKey) => {
    if (!resizingColumn) return;

    const table = tableRef.current;
    if (!table) return;

    const th = table.querySelector(`th[data-column="${columnKey}"]`);
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    
    // Set minimum width to prevent columns from becoming too narrow
    if (newWidth >= 100) {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    }
  };

  // Add these event handlers for mouse events
  const handleMouseDown = (e, columnKey) => {
    setResizingColumn(columnKey);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setResizingColumn(null);
  };

  const handleMouseMove = (e) => {
    if (resizingColumn) {
      handleColumnResize(e, resizingColumn);
    }
  };

  // Add effect to handle mouse events
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Çalışan Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.zamButton} onClick={() => setShowZamModal(true)}>
                <FaMoneyBillWave className={styles.buttonIcon} /> Toplu Zam
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{employees.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Çalışan</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Çalışan</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Çalışan</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="İsim, soyisim veya TC no ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearch} 
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedEmployee && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedEmployee.eName} {selectedEmployee.eSurname}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedEmployee.Active === 1 
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedEmployee.Active === 1 ? 'Aktif' : 'İnaktif'}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button className={`${styles.actionButton} ${styles.salaryButton}`} onClick={openSalaryModal}>
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>Maaş</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.paymentsButton}`} onClick={openPaymentsModal}>
                <FaFileInvoiceDollar className={styles.actionIcon} />
                <span>Ödemeler</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.budgetButton}`} onClick={openBudgetPricingModal}>
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>Çalışan Bütçe Ücretlendirme</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table 
                  ref={tableRef}
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th 
                            key={key}
                            data-column={key}
                            className={`${resizingColumn === key ? styles.resizing : styles.resizable}`}
                            style={{ width: columnWidths[key] || 'auto' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                          >
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => (
                        !emp.isEmpty && (
                          <tr 
                            key={emp.id} 
                            onClick={() => handleEmployeeSelect(emp)}
                            className={selectedEmployee && selectedEmployee.id === emp.id ? styles.selectedRow : ''}
                          >
                            {Object.keys(columns).map(key => 
                              visibleColumns[key] && (
                                <td key={key} title={emp[key]}>
                                  {key === 'Active' ? (
                                    <span className={`${styles.statusBadge} ${
                                      emp.Active === undefined || emp.Active === null || isNaN(emp.Active)
                                        ? styles.statusNoData
                                        : emp.Active === 1
                                          ? styles.statusActive
                                          : styles.statusInactive
                                    }`}>
                                      {emp.Active === undefined || emp.Active === null || isNaN(emp.Active)
                                        ? '-'
                                        : emp.Active === 1
                                          ? 'aktif'
                                          : 'inaktif'}
                                    </span>
                                  ) : key === 'isHandicappedPerson' || key === 'isIntern' || key === 'isRetired' ? (
                                    truncateText(emp[key] ? 'Evet' : 'Hayır')
                                  ) : key === 'nationality' ? (
                                    truncateText(emp[key] === 173 ? 'Turkey' : emp[key])
                                  ) : (
                                    truncateText(emp[key])
                                  )}
                                </td>
                              )
                            )}
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun çalışan bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {showSalaryModal && (
        <SalaryModal
          salaries={salaries}
          setSalaries={setSalaries}
          selectedEmployee={selectedEmployee}
          onClose={() => setShowSalaryModal(false)}
        />
      )}
      
      {showPaymentsModal && (
        <PaymentsModal
          payments={payments}
          setPayments={setPayments}
          selectedEmployee={selectedEmployee}
          onClose={() => setShowPaymentsModal(false)}
        />
      )}
      
      <BudgetPricingModal
        selectedEmployee={selectedEmployee}
        onClose={() => setShowBudgetPricingModal(false)}
        show={showBudgetPricingModal}
      />

      {showZamModal && (
        <ZamModal
          onClose={() => setShowZamModal(false)}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default EmployeeManagementPage;